import { useTranslation } from 'react-i18next';
import { isNativeApp } from '@/lib/isNativeApp';
import { useIsNarrowViewport } from '@/lib/useIsNarrowViewport';
import { Tooltip } from '@heroui/react';
import { TooltipTarget } from '@/components/ui/TooltipTarget';

/**
 * "Download for Windows" — a quiet icon button that sits with the theme /
 * language toggles in the rail foot.
 *
 * Renders ONLY in a regular DESKTOP browser:
 *   - pointless inside the Electron shell (they already have it) and inside
 *     the Capacitor mobile app — both feature-detected at runtime;
 *   - pointless on a phone, where a Windows installer can't be used at all.
 *     It also cost real space there: the header's server rail is sized by
 *     what's left after this cluster, and this button was squeezing avatars
 *     into the buttons next to it.
 *
 * The mobile check is a media query rather than a user-agent sniff, so a
 * narrow desktop window keeps the button (its owner CAN install it) and the
 * layout consequence follows the actual available width.
 *
 * The href is the permanent short link — nginx 302s it to the current
 * installer, which the release script refreshes on every desktop release, so
 * this never needs touching when a version ships.
 */
const DOWNLOAD_URL = 'https://bunker.cispracs.ru/download';

export function DownloadAppButton({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const narrow = useIsNarrowViewport();
  // The Electron shell exposes this bridge; the mobile shell is Capacitor.
  const inDesktopShell = typeof window !== 'undefined' && window.bunkerDesktop?.isDesktop === true;
  if (inDesktopShell || isNativeApp() || narrow) return null;

  return (
    // A plain <a> is NOT a HeroUI interactive component: passed straight to
    // <Tooltip> the tooltip never appears. TooltipTarget makes the link itself
    // the trigger (Tooltip.Trigger would wrap it in a second focusable
    // div role="button" — two Tab stops, the name read twice).
    <Tooltip delay={250} closeDelay={0}>
      <TooltipTarget>
        <a
          href={DOWNLOAD_URL}
          // The name sits on the <a> itself: its only other content is an
          // aria-hidden SVG (WCAG 4.1.2).
          aria-label={t('download.aria')}
          // The server sends the .exe as an attachment; keep the SPA mounted
          // (no navigation) and never hand window.opener to the download.
          download
          rel="noopener"
          className={
            'group grid size-8 shrink-0 place-items-center rounded-lg text-ink-dim transition-colors hover:bg-default/60 hover:text-ink [@media(pointer:coarse)]:size-11 ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ' +
            className
          }
        >
        {/* Monitor with a download arrow — stroke-only, inherits currentColor
            like the neighbouring theme/language glyphs. */}
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="3" width="20" height="13" rx="2" />
          <path d="M8 21h8M12 16v5" />
          <path
            d="M12 6v5m0 0 2.2-2.2M12 11 9.8 8.8"
            className="transition-transform duration-300 ease-out group-hover:translate-y-0.5"
          />
        </svg>
        </a>
      </TooltipTarget>
      <Tooltip.Content showArrow className="text-[11px]">
        <Tooltip.Arrow />
        {t('download.tooltip')}
      </Tooltip.Content>
    </Tooltip>
  );
}
